<template>
    <div v-if="groups.length" class="sources-tree">
        <div
            v-for="group in groups"
            :key="group.key"
            class="source-group"
            :class="{ 'source-group--open': isOpen(group.key) }"
        >
            <button
                type="button"
                class="source-group-head"
                :aria-expanded="isOpen(group.key)"
                @click="toggle(group.key)"
            >
                <span class="caret" aria-hidden="true">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </span>
                <span class="source-tag">
                    <ChatIcon name="database" :size="11" color="#FFFFFF" />
                    <span>ИСТОЧНИК</span>
                </span>
                <span class="source-title">{{ group.title }}</span>
                <span class="source-counter">
                    {{ group.items.length }}
                    {{ group.hasLinks
                        ? pluralize(group.items.length, ['ссылка', 'ссылки', 'ссылок'])
                        : pluralize(group.items.length, ['запрос', 'запроса', 'запросов']) }}
                </span>
                <span v-if="group.totalRecords" class="source-records">· {{ formatRecords(group.totalRecords) }}</span>
            </button>

            <div v-if="isOpen(group.key)" class="source-group-body">
                <template v-for="(item, idx) in group.items" :key="`${group.key}-${idx}`">
                    <a
                        v-if="item.url"
                        class="source-item source-item--link"
                        :href="item.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        :title="item.url"
                    >
                        <span class="source-item-dot" aria-hidden="true" />
                        <span class="source-item-period">{{ item.title || item.url }}</span>
                        <span class="source-item-host">{{ formatHost(item.url) }}</span>
                    </a>
                    <div v-else class="source-item">
                        <span class="source-item-dot" aria-hidden="true" />
                        <span class="source-item-period">{{ item.period || 'без описания' }}</span>
                        <span v-if="item.records" class="source-item-records">{{ item.records }}</span>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, ref } from 'vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'

export default {
    name: 'ChatMessageSources',
    components: { ChatIcon },
    props: {
        sources: { type: Array, default: () => [] },
    },
    setup(props) {
        const openKeys = ref(new Set())

        const groups = computed(() => {
            const map = new Map()
            const list = Array.isArray(props.sources) ? props.sources : []
            list.forEach((src) => {
                if (!src) return
                const key = src.code || src.type || 'source'
                const title = src.type || src.code || 'Источник'
                if (!map.has(key)) {
                    map.set(key, { key, title, items: [], totalRecords: 0, hasLinks: false })
                }
                const group = map.get(key)
                group.items.push(src)
                if (src.url) group.hasLinks = true
                const num = parseRecords(src.records)
                if (num != null) group.totalRecords += num
            })
            return Array.from(map.values())
        })

        function toggle(key) {
            const next = new Set(openKeys.value)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            openKeys.value = next
        }

        function isOpen(key) {
            return openKeys.value.has(key)
        }

        function parseRecords(text) {
            if (!text) return null
            const match = String(text).match(/(\d[\d\s]*)/)
            if (!match) return null
            const n = Number(match[1].replace(/\s+/g, ''))
            return Number.isFinite(n) ? n : null
        }

        function formatRecords(total) {
            if (!Number.isFinite(total) || total <= 0) return ''
            const formatted = total.toLocaleString('ru-RU')
            return `${formatted} ${pluralize(total, ['запись', 'записи', 'записей'])}`
        }

        function pluralize(n, forms) {
            const abs = Math.abs(n) % 100
            const last = abs % 10
            if (abs > 10 && abs < 20) return forms[2]
            if (last > 1 && last < 5) return forms[1]
            if (last === 1) return forms[0]
            return forms[2]
        }

        /**
         * Извлекает host из URL для компактного отображения справа от названия.
         * Если URL невалиден — возвращает пустую строку, чтобы UI не ломался.
         */
        function formatHost(url) {
            if (!url) return ''
            try {
                const u = new URL(url)
                return u.host.replace(/^www\./, '')
            } catch (e) {
                return ''
            }
        }

        return { groups, toggle, isOpen, pluralize, formatRecords, formatHost }
    },
}
</script>

<style scoped>
.sources-tree {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 12px;
}

.source-group {
    background: #F7F5F0;
    border: 1px solid #E8E6E0;
    border-radius: 6px;
    overflow: hidden;
}

.source-group-head {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    background: transparent;
    border: 0;
    padding: 6px 10px;
    font-size: 11px;
    color: #5F5E5A;
    cursor: pointer;
    text-align: left;
    font: inherit;
}

.source-group-head:hover {
    background: #F1EFE8;
}

.caret {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #888780;
    transition: transform 0.15s ease;
    width: 12px;
    height: 12px;
    flex-shrink: 0;
}

.source-group--open .caret {
    transform: rotate(180deg);
}

.source-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #23282C;
    color: white;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 0.5px;
    padding: 3px 8px;
    border-radius: 4px;
    flex-shrink: 0;
}

.source-title {
    color: #23282C;
    font-weight: 600;
    font-size: 11px;
}

.source-counter {
    color: #5F5E5A;
}

.source-records {
    color: #888780;
}

.source-group-body {
    border-top: 1px solid #E8E6E0;
    padding: 4px 12px 8px 36px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.source-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: #5F5E5A;
    padding: 3px 0;
}

.source-item-dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #C4C2BC;
    flex-shrink: 0;
}

.source-item-period {
    color: #23282C;
}

.source-item-records {
    color: #888780;
}

.source-item-records::before {
    content: '· ';
    color: #C4C2BC;
}

.source-item--link {
    text-decoration: none;
    color: inherit;
    transition: background-color 0.12s ease;
    border-radius: 4px;
    margin: -2px -4px;
    padding: 5px 4px;
}

.source-item--link:hover {
    background: #F1EFE8;
}

.source-item--link:hover .source-item-period {
    text-decoration: underline;
    color: #0F1115;
}

.source-item-host {
    color: #888780;
    font-size: 10.5px;
    margin-left: auto;
    padding-left: 8px;
    flex-shrink: 0;
}
</style>
